/* eslint-disable react-refresh/only-export-components */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Plus, Trash2 } from "lucide-react";
import {
  useCanAccess,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useId, useState, type ReactNode } from "react";
import { AccessDenied } from "@/components/admin/access-denied";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import {
  DEFAULT_KEY_PREFIX,
  MAX_KEY_TEXT,
  keyExample,
  keyProblem,
  visibleSpaces,
} from "../deals/onecFormat";
import { byPosition } from "../projects/byPosition";
import type { CrmDataProvider } from "../providers/types";
import { useConfigurationUpdater } from "../root/ConfigurationContext";
import type { Project } from "../types";
import { BitrixPanel } from "./BitrixPanel";
import { MailboxForm, PersonalMailboxes } from "./MailboxForm";
import { SettingsTabs } from "./SettingsTabs";

/**
 * Настройки → Подключения (owner only): the bot for Telegram notices, the
 * keys of the inbound hooks (site forms, PBX), the telephony settings and 1С
 * (address, key rule, on/off in the config; login and password as secrets),
 * Bitrix24 (the inbound webhook as a secret; the mode, the move and the sync
 * in BitrixPanel), the company mailbox and the employees' own mailboxes
 * (mail, write-only).
 * Secrets are write-only: the page knows only whether each one is filled
 * («введён ✓»). An inbound key is made by the server and shown once, with
 * the full address to paste into the site or the PBX.
 */
export const ConnectionsPage = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const isMobile = useIsMobile();
  if (isPending) return null;
  if (!canAccess) return <AccessDenied />;
  return (
    <div className="mt-4 pb-20 flex flex-col gap-6">
      {isMobile ? null : <SettingsTabs />}
      <Connections />
    </div>
  );
};

ConnectionsPage.path = "/settings/connections";

type TelephonyLine = { number: string; project_id: number | string | null };
type Telephony = { provider?: string; lines?: TelephonyLine[] };
type OneC = {
  enabled?: boolean;
  odataUrl?: string;
  dealKeyPrefix?: string;
  dealKeySuffix?: string;
};
type MailConfig = Record<string, unknown> & { leadsFromUnknown?: boolean };
type StoredConfig = Record<string, unknown> & {
  publicUrl?: string;
  mail?: MailConfig;
  telephony?: Telephony;
  onec?: OneC;
};

const SECRETS_KEY = ["vv-secrets"];

/** where the hooks live: publicUrl, or this page's own address */
export const crmBase = (publicUrl: string | undefined | null) =>
  (publicUrl?.trim() || window.location.origin).replace(/\/+$/, "");

const errorMessage = (error: unknown) =>
  (error as { body?: { message?: string } })?.body?.message ??
  (error as Error)?.message ??
  String(error);

/** Saves keys of configuration.config; every other key is kept. */
const useSaveConfig = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const updateConfiguration = useConfigurationUpdater();
  const queryClient = useQueryClient();
  return async (patch: Partial<StoredConfig>) => {
    let stored: StoredConfig = {};
    let exists = true;
    try {
      const { data } = await dataProvider.getOne("configuration", { id: 1 });
      stored = (data?.config as StoredConfig) ?? {};
    } catch {
      exists = false;
    }
    const config = { ...stored, ...patch };
    const { data } = exists
      ? await dataProvider.update("configuration", {
          id: 1,
          data: { config },
          previousData: { id: 1, config: stored },
        })
      : await dataProvider.create("configuration", {
          data: { id: 1, config },
        });
    if (data?.config) updateConfiguration(data.config);
    await queryClient.invalidateQueries({ queryKey: ["configuration"] });
  };
};

const Connections = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: secrets } = useQuery({
    queryKey: SECRETS_KEY,
    queryFn: () => dataProvider.getSecrets(),
  });
  const { data: configuration, isPending: configPending } = useGetOne<{
    id: number;
    config: StoredConfig;
  }>("configuration", { id: 1 }, { retry: false });
  const config = configuration?.config ?? {};
  const base = crmBase(config.publicUrl);
  const isSet = (key: string) => secrets?.includes(key) === true;

  return (
    <div className="flex flex-col gap-4 max-w-2xl min-w-0">
      <h2 className="vv-heading">{translate("crm.connections.title")}</h2>

      <Section title={translate("crm.connections.public_url")}>
        {/* the forms start from the stored values, so they wait for them */}
        {configPending ? null : (
          <PublicUrlField value={config.publicUrl ?? ""} />
        )}
      </Section>

      <Section
        title={translate("crm.connections.telegram.title")}
        hint={translate("crm.connections.telegram.hint")}
      >
        <p className="text-sm">{translate("crm.connections.telegram.steps")}</p>
        <SecretField
          name="telegram_bot_token"
          label={translate("crm.connections.telegram.bot_token")}
          isSet={isSet("telegram_bot_token")}
        />
        <SecretField
          name="telegram_api_base"
          label={translate("crm.connections.telegram.api_base")}
          hint={translate("crm.connections.telegram.api_base_hint")}
          isSet={isSet("telegram_api_base")}
          plain
        />
      </Section>

      <Section
        title={translate("crm.connections.leads.title")}
        hint={translate("crm.connections.leads.hint")}
      >
        <InboundKey
          name="lead_hook_token"
          label={translate("crm.connections.leads.token")}
          isSet={isSet("lead_hook_token")}
          address={{
            label: translate("crm.connections.leads.url_label"),
            value: `${base}/api/hooks/lead`,
          }}
          reveal={(token) => [
            {
              label: translate("crm.connections.leads.url_label"),
              value: `${base}/api/hooks/lead?token=${token}`,
            },
            { label: "X-Hook-Token", value: token },
          ]}
          hint={translate("crm.connections.leads.header_hint")}
        />
      </Section>

      <Section title={translate("crm.connections.telephony.title")}>
        {configPending ? null : <TelephonyForm telephony={config.telephony} />}
        <div className="border-t pt-4 flex flex-col gap-4">
          <p className="text-sm font-medium">
            {translate("crm.connections.telephony.provider_megafon")}
          </p>
          <p className="text-sm text-muted-foreground">
            {translate("crm.connections.telephony.megafon_hint")}
          </p>
          <CopyLine
            label={translate("crm.connections.telephony.megafon_url_label")}
            value={`${base}/api/hooks/megafon`}
          />
          <InboundKey
            name="megafon_crm_token"
            label={translate("crm.connections.telephony.megafon_crm_token")}
            isSet={isSet("megafon_crm_token")}
            reveal={(token) => [
              {
                label: translate("crm.connections.telephony.megafon_crm_token"),
                value: token,
              },
            ]}
            pasteToo
          />
          <SecretField
            name="megafon_api_url"
            label={translate("crm.connections.telephony.megafon_api_url")}
            isSet={isSet("megafon_api_url")}
            plain
          />
          <SecretField
            name="megafon_api_key"
            label={translate("crm.connections.telephony.megafon_api_key")}
            isSet={isSet("megafon_api_key")}
          />
        </div>
        <div className="border-t pt-4 flex flex-col gap-4">
          <p className="text-sm font-medium">
            {translate("crm.connections.telephony.own_title")}
          </p>
          <InboundKey
            name="telephony_token"
            label={translate("crm.connections.telephony.own_token")}
            isSet={isSet("telephony_token")}
            hint={translate("crm.connections.telephony.own_hint")}
            address={{
              label: translate("crm.connections.telephony.own_url_label"),
              value: `${base}/api/telephony/events`,
            }}
            reveal={(token) => [
              {
                label: translate("crm.connections.telephony.own_url_label"),
                value: `${base}/api/telephony/events`,
              },
              { label: "X-Telephony-Token", value: token },
            ]}
          />
        </div>
      </Section>

      <Section
        title={translate("crm.connections.onec.title")}
        hint={translate("crm.connections.onec.hint")}
      >
        <p className="text-sm">{translate("crm.connections.onec.steps")}</p>
        {configPending ? null : <OneCForm onec={config.onec} />}
        <SecretField
          name="onec_user"
          label={translate("crm.connections.onec.user")}
          isSet={isSet("onec_user")}
          plain
        />
        <SecretField
          name="onec_password"
          label={translate("crm.connections.onec.password")}
          isSet={isSet("onec_password")}
        />
        <OneCCheck />
      </Section>

      <Section
        title={translate("crm.bitrix.title")}
        hint={translate("crm.bitrix.hint")}
      >
        <p className="text-sm">{translate("crm.bitrix.steps")}</p>
        <SecretField
          name="bitrix_webhook"
          label={translate("crm.bitrix.webhook")}
          hint={translate("crm.bitrix.webhook_hint")}
          isSet={isSet("bitrix_webhook")}
        />
        <BitrixPanel hasWebhook={isSet("bitrix_webhook")} />
      </Section>

      <Section
        title={translate("crm.connections.mail.title")}
        hint={translate("crm.connections.mail.hint")}
      >
        <MailboxForm scope="company" />
        {configPending ? null : (
          <LeadsFromUnknown
            on={config.mail?.leadsFromUnknown === true}
            mail={config.mail}
          />
        )}
        <PersonalMailboxes />
      </Section>
    </div>
  );
};

/** config.mail.leadsFromUnknown: a letter from an unknown address becomes a lead */
const LeadsFromUnknown = ({ on, mail }: { on: boolean; mail?: MailConfig }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const saveConfig = useSaveConfig();
  const [checked, setChecked] = useState(on);
  const [busy, setBusy] = useState(false);
  const toggle = async (next: boolean) => {
    setBusy(true);
    setChecked(next);
    try {
      await saveConfig({ mail: { ...mail, leadsFromUnknown: next } });
      notify("crm.connections.saved", { type: "success" });
    } catch (error) {
      setChecked(!next);
      notify(errorMessage(error), {
        type: "error",
        messageArgs: { _: errorMessage(error) },
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-1 border-t pt-4 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm">
          {translate("crm.connections.mail.leads_from_unknown")}
        </span>
        <Switch
          checked={checked}
          disabled={busy}
          onCheckedChange={(next) => void toggle(next)}
          aria-label={translate("crm.connections.mail.leads_from_unknown")}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("crm.connections.mail.leads_from_unknown_hint")}
      </p>
    </div>
  );
};

const Section = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) => (
  <Card>
    <CardContent className="flex flex-col gap-4 min-w-0">
      <div className="space-y-1">
        <h3 className="text-base font-semibold">{title}</h3>
        {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
      </div>
      {children}
    </CardContent>
  </Card>
);

const Status = ({ isSet, name }: { isSet: boolean; name: string }) => {
  const translate = useTranslate();
  return (
    <span
      data-testid={`status-${name}`}
      className={cn(
        "text-xs shrink-0",
        isSet ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {translate(isSet ? "crm.connections.is_set" : "crm.connections.not_set")}
    </span>
  );
};

/** A write-only secret: a field to type a new value, «Сохранить», «Удалить». */
const SecretField = ({
  name,
  label,
  hint,
  isSet,
  plain = false,
  showStatus = true,
}: {
  name: string;
  label: string;
  hint?: string;
  isSet: boolean;
  /** an address: shown while typed (still never read back) */
  plain?: boolean;
  /** off when the key's status is already shown above */
  showStatus?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const id = useId();

  const save = async (next: string) => {
    setBusy(true);
    try {
      await dataProvider.setSecret(name, next);
      setValue("");
      notify(next ? "crm.connections.saved" : "crm.connections.removed", {
        type: "success",
      });
    } catch (error) {
      notify(errorMessage(error), {
        type: "error",
        messageArgs: { _: errorMessage(error) },
      });
    } finally {
      setBusy(false);
      await queryClient.invalidateQueries({ queryKey: SECRETS_KEY });
    }
  };

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-sm">
          {label}
        </label>
        {showStatus ? <Status isSet={isSet} name={name} /> : null}
      </div>
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (value.trim()) void save(value.trim());
        }}
      >
        <Input
          id={id}
          type={plain ? "text" : "password"}
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={
            isSet ? translate("crm.connections.replace_placeholder") : ""
          }
          className="h-9 min-w-0 flex-1 basis-48"
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          className="h-9"
          disabled={busy || !value.trim()}
        >
          {translate("crm.connections.save")}
        </Button>
        {isSet ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9"
            disabled={busy}
            aria-label={`${translate("crm.connections.remove")}: ${label}`}
            onClick={() => void save("")}
          >
            {translate("crm.connections.remove")}
          </Button>
        ) : null}
      </form>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
};

/** A key other systems send us: «Создать новый» shows it once, ready to copy. */
const InboundKey = ({
  name,
  label,
  isSet,
  hint,
  address,
  reveal,
  pasteToo = false,
}: {
  name: string;
  label: string;
  isSet: boolean;
  hint?: string;
  address?: { label: string; value: string };
  reveal: (token: string) => { label: string; value: string }[];
  /** the other side may also give its own key (MegaFon's ВАТС) */
  pasteToo?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    setConfirming(false);
    try {
      setToken(await dataProvider.generateSecret(name));
    } catch (error) {
      notify(errorMessage(error), {
        type: "error",
        messageArgs: { _: errorMessage(error) },
      });
    } finally {
      setBusy(false);
      await queryClient.invalidateQueries({ queryKey: SECRETS_KEY });
    }
  };

  return (
    <div className="flex flex-col gap-2 min-w-0" data-key={name}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm">{label}</span>
        <Status isSet={isSet || token != null} name={name} />
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {address && !token ? (
        <CopyLine label={address.label} value={address.value} />
      ) : null}
      {token ? (
        <div className="flex flex-col gap-2 rounded-md border border-primary/50 p-3 min-w-0">
          <p className="text-sm font-medium">
            {translate("crm.connections.generated_once")}
          </p>
          {reveal(token).map((line) => (
            <CopyLine key={line.label} label={line.label} value={line.value} />
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {confirming ? (
          <>
            <span className="text-sm">
              {translate("crm.connections.generate_confirm")}
            </span>
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => void generate()}
            >
              {translate("crm.connections.generate_yes")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            aria-label={`${translate("crm.connections.generate")}: ${label}`}
            onClick={() =>
              isSet || token ? setConfirming(true) : void generate()
            }
          >
            {translate("crm.connections.generate")}
          </Button>
        )}
      </div>
      {pasteToo ? (
        <SecretField
          name={name}
          label={translate("crm.connections.paste_own")}
          isSet={isSet}
          showStatus={false}
        />
      ) : null}
    </div>
  );
};

/** A value to copy: an address, a key, a header. */
const CopyLine = ({ label, value }: { label: string; value: string }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      notify("crm.connections.copy_failed", { type: "warning" });
    }
  };
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-start gap-2 min-w-0">
        <code
          className="flex-1 min-w-0 break-all rounded bg-muted px-2 py-1.5 text-xs"
          data-copy={label}
        >
          {value}
        </code>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 shrink-0"
          aria-label={`${translate("crm.connections.copy")}: ${label}`}
          onClick={() => void copy()}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </Button>
      </div>
    </div>
  );
};

/** configuration.config.publicUrl: links in the notices and the hook addresses. */
const PublicUrlField = ({ value }: { value: string }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const saveConfig = useSaveConfig();
  const [text, setText] = useState(value);
  const [busy, setBusy] = useState(false);
  const id = useId();
  useEffect(() => setText(value), [value]);

  const save = async () => {
    const next = text.trim().replace(/\/+$/, "");
    if (next && !/^https?:\/\/[^/\s]+/i.test(next)) {
      notify("crm.connections.public_url_bad", { type: "error" });
      return;
    }
    setBusy(true);
    try {
      await saveConfig({ publicUrl: next });
      notify("crm.connections.saved", { type: "success" });
    } catch (error) {
      notify(errorMessage(error), {
        type: "error",
        messageArgs: { _: errorMessage(error) },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-1.5 min-w-0"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <label htmlFor={id} className="sr-only">
        {translate("crm.connections.public_url")}
      </label>
      <div className="flex flex-wrap gap-2">
        <Input
          id={id}
          type="url"
          value={text}
          placeholder={window.location.origin}
          onChange={(event) => setText(event.target.value)}
          className="h-9 min-w-0 flex-1 basis-48"
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          className="h-9"
          disabled={busy || text.trim() === value}
        >
          {translate("crm.connections.save")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("crm.connections.public_url_hint")}
      </p>
    </form>
  );
};

/** configuration.config.telephony: the PBX and which company number is which project. */
const TelephonyForm = ({ telephony }: { telephony?: Telephony }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const saveConfig = useSaveConfig();
  const { data: projectData } = useGetList<Project>("projects", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "position", order: "ASC" },
  });
  const projects = [...(projectData ?? [])]
    .filter((p) => !p.archived)
    .sort(byPosition);
  const [provider, setProvider] = useState(telephony?.provider ?? "none");
  const [lines, setLines] = useState<TelephonyLine[]>(telephony?.lines ?? []);
  const [busy, setBusy] = useState(false);
  const providerId = useId();
  // a refetch gives a new object: only a really changed stored value resets
  // the form (unsaved edits stay otherwise)
  const stored = JSON.stringify(telephony ?? null);
  useEffect(() => {
    const t = JSON.parse(stored) as Telephony | null;
    setProvider(t?.provider ?? "none");
    setLines(t?.lines ?? []);
  }, [stored]);

  const setLine = (index: number, patch: Partial<TelephonyLine>) =>
    setLines((all) =>
      all.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );

  const save = async () => {
    const clean = lines
      .map((line) => ({
        number: line.number.trim(),
        project_id: Number(line.project_id),
      }))
      .filter((line) => line.number && Number.isSafeInteger(line.project_id));
    setBusy(true);
    try {
      await saveConfig({ telephony: { provider, lines: clean } });
      setLines(clean);
      notify("crm.connections.telephony.saved", { type: "success" });
    } catch (error) {
      notify(errorMessage(error), {
        type: "error",
        messageArgs: { _: errorMessage(error) },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-3 min-w-0"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={providerId} className="text-sm">
          {translate("crm.connections.telephony.provider")}
        </label>
        <select
          id={providerId}
          value={provider}
          onChange={(event) => setProvider(event.target.value)}
          className="h-9 rounded-md border bg-card px-2 text-sm"
        >
          <option value="none">
            {translate("crm.connections.telephony.provider_none")}
          </option>
          <option value="megafon">
            {translate("crm.connections.telephony.provider_megafon")}
          </option>
        </select>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm">
          {translate("crm.connections.telephony.lines")}
        </p>
        <p className="text-xs text-muted-foreground">
          {translate("crm.connections.telephony.lines_hint")}
        </p>
        {lines.map((line, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <Input
              value={line.number}
              aria-label={`${translate("crm.connections.telephony.number")} ${index + 1}`}
              placeholder="+7 900 000-00-00"
              inputMode="tel"
              onChange={(event) =>
                setLine(index, { number: event.target.value })
              }
              className="h-9 min-w-0 flex-1 basis-40"
            />
            <select
              aria-label={`${translate("crm.connections.telephony.project")} ${index + 1}`}
              value={line.project_id == null ? "" : String(line.project_id)}
              onChange={(event) =>
                setLine(index, { project_id: event.target.value || null })
              }
              className="h-9 min-w-0 flex-1 basis-32 rounded-md border bg-card px-2 text-sm"
            >
              <option value="">
                {translate("crm.connections.telephony.project")}
              </option>
              {projects.map((project) => (
                <option key={project.id} value={String(project.id)}>
                  {project.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9"
              aria-label={`${translate("crm.connections.telephony.remove_line")} ${index + 1}`}
              onClick={() =>
                setLines((all) => all.filter((_, i) => i !== index))
              }
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              setLines((all) => [
                ...all,
                { number: "", project_id: projects[0]?.id ?? null },
              ])
            }
          >
            <Plus className="size-4" />
            {translate("crm.connections.telephony.add_line")}
          </Button>
        </div>
      </div>
      <div>
        <Button type="submit" size="sm" disabled={busy}>
          {translate("crm.connections.telephony.save")}
        </Button>
      </div>
    </form>
  );
};

/** configuration.config.onec: on/off, the OData address and the deal key (crm-api checks them again). */
const OneCForm = ({ onec }: { onec?: OneC }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(onec?.enabled === true);
  const [url, setUrl] = useState(onec?.odataUrl ?? "");
  const [prefix, setPrefix] = useState(
    onec?.dealKeyPrefix || DEFAULT_KEY_PREFIX,
  );
  const [suffix, setSuffix] = useState(onec?.dealKeySuffix ?? "");
  const [busy, setBusy] = useState(false);
  const urlId = useId();
  const keyId = useId();
  const stored = JSON.stringify(onec ?? null);
  useEffect(() => {
    const o = JSON.parse(stored) as OneC | null;
    setEnabled(o?.enabled === true);
    setUrl(o?.odataUrl ?? "");
    setPrefix(o?.dealKeyPrefix || DEFAULT_KEY_PREFIX);
    setSuffix(o?.dealKeySuffix ?? "");
  }, [stored]);

  const save = async () => {
    const odataUrl = url.trim().replace(/\/+$/, "");
    if (odataUrl && !/^https?:\/\/[^/\s]+/i.test(odataUrl)) {
      notify("crm.connections.onec.bad_url", { type: "error" });
      return;
    }
    const problem = keyProblem(prefix, suffix);
    if (problem) {
      notify(problem, { type: "error" });
      return;
    }
    setBusy(true);
    try {
      const { passwordCleared } = await dataProvider.saveOneC({
        enabled,
        odataUrl,
        // exactly as typed: «Сделка № » with its space is a different key
        dealKeyPrefix: prefix,
        dealKeySuffix: suffix,
      });
      setUrl(odataUrl);
      notify(
        passwordCleared
          ? "crm.connections.onec.password_cleared"
          : "crm.connections.onec.saved",
        { type: passwordCleared ? "warning" : "success" },
      );
      await queryClient.invalidateQueries({ queryKey: ["configuration"] });
      await queryClient.invalidateQueries({ queryKey: SECRETS_KEY });
    } catch (error) {
      notify(errorMessage(error), {
        type: "error",
        messageArgs: { _: errorMessage(error) },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-3 min-w-0"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm">
          {translate("crm.connections.onec.enabled")}
        </span>
        <Switch
          checked={enabled}
          onCheckedChange={(checked) => setEnabled(checked)}
          aria-label={translate("crm.connections.onec.enabled")}
        />
      </div>
      <div className="flex flex-col gap-1.5 min-w-0">
        <label htmlFor={urlId} className="text-sm">
          {translate("crm.connections.onec.odata_url")}
        </label>
        <Input
          id={urlId}
          type="url"
          autoComplete="off"
          spellCheck={false}
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          className="h-9 min-w-0"
        />
        <p className="text-xs text-muted-foreground break-words">
          {translate("crm.connections.onec.odata_url_hint")}
        </p>
      </div>
      <fieldset className="flex flex-col gap-1.5 min-w-0">
        <legend id={keyId} className="text-sm mb-1.5">
          {translate("crm.connections.onec.key")}
        </legend>
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Input
            aria-label={translate("crm.connections.onec.prefix")}
            autoComplete="off"
            spellCheck={false}
            maxLength={MAX_KEY_TEXT}
            value={prefix}
            onChange={(event) => setPrefix(event.target.value)}
            className="h-9 w-28 min-w-0"
          />
          <span className="text-sm text-muted-foreground">
            {translate("crm.connections.onec.number")}
          </span>
          <Input
            aria-label={translate("crm.connections.onec.suffix")}
            placeholder={translate("crm.connections.onec.suffix_placeholder")}
            autoComplete="off"
            spellCheck={false}
            maxLength={MAX_KEY_TEXT}
            value={suffix}
            onChange={(event) => setSuffix(event.target.value)}
            className="h-9 w-32 min-w-0"
          />
        </div>
        <p className="text-sm">
          {translate("crm.connections.onec.example")}{" "}
          <code
            data-testid="onec-key-example"
            className="rounded bg-muted px-1.5 py-0.5 text-xs break-all"
          >
            {visibleSpaces(keyExample(prefix, suffix, 101))}
          </code>
        </p>
        <p className="text-xs text-muted-foreground">
          {translate("crm.connections.onec.key_hint")}
        </p>
      </fieldset>
      <div>
        <Button type="submit" size="sm" disabled={busy}>
          {translate("crm.connections.onec.save")}
        </Button>
      </div>
    </form>
  );
};

/** «Проверить подключение»: one request to 1С with the saved settings, the answer below. */
const OneCCheck = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(
    null,
  );

  const check = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { sample } = await dataProvider.testOneC();
      const date = sample?.date
        ? sample.date.split("-").reverse().join(".")
        : "";
      setResult({
        ok: true,
        text: sample
          ? translate("crm.connections.onec.check_ok", {
              number: sample.number ?? "",
              date,
            })
          : translate("crm.connections.onec.check_ok_empty"),
      });
    } catch (error) {
      setResult({ ok: false, text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t pt-4 flex flex-col gap-2 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void check()}
        >
          {translate("crm.connections.onec.check")}
        </Button>
        <span className="text-xs text-muted-foreground">
          {translate("crm.connections.onec.check_hint")}
        </span>
      </div>
      {result ? (
        <p
          role="status"
          data-testid="onec-check"
          className={cn(
            "text-sm break-words",
            result.ok ? "text-foreground" : "text-destructive",
          )}
        >
          {result.text}
        </p>
      ) : null}
    </div>
  );
};
